import React, { useState, useEffect } from 'react';
import { User, Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import ReportsPage from '../pages/ReportsPage';
import LoadingSpinner from './modals/LoadingSpinner';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function ReportAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // const [userData, setUserData] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/verify`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        // const data = 
        await response.json();
        setIsAuthenticated(true);
        // setUserData(data.user);
      } else {
        setIsAuthenticated(false);
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async () => {
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Login successful!');
        // setUserData(data.user);
        setTimeout(() => {
          setIsAuthenticated(true);
          setEmail('');
          setPassword('');
        }, 1000);
      } else {
        setError(data.error || 'Login failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  // const handleLogout = async () => {
  //   try {
  //     await fetch(`${API_BASE_URL}/api/v1/auth/logout`, {
  //       method: 'POST',
  //       credentials: 'include'
  //     });
  //     setIsAuthenticated(false);
  //     setUserData(null);
  //     setEmail('');
  //     setPassword('');
  //   } catch (err) {
  //     console.error('Logout failed:', err);
  //   }
  // };

  if (isLoading) {
    return (
      <LoadingSpinner />
    );
  }

  if (isAuthenticated) {
    return <ReportsPage />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: "#fff" }}>
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl shadow-xl overflow-hidden" style={{ borderColor: "#103D4B", borderWidth: "2px" }}>
          <div className="p-8 text-center text-black" style={{ backgroundColor: "#fff", borderBottom: "2px solid #103D4B" }}>
            <h1 className="text-3xl font-bold mb-2">Authentication Required</h1>
            <p className="text-black">Please sign in to continue</p>
          </div>

          <div className="p-8">
            {error && (
              <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl flex items-center gap-3 animate-pulse">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}

            {success && (
              <div className="mb-6 p-4 bg-green-50 border-2 border-green-200 rounded-xl flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                <p className="text-green-800 text-sm">{success}</p>
              </div>
            )}

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Username</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:border-[#103D4B] focus:ring-2 focus:ring-[#103D4B] transition-all outline-none"
                    placeholder="Enter your email"
                    style={{ borderColor: "#103D4B" }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                    </svg>
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="w-full pl-12 pr-12 py-3 border border-gray-300 rounded-xl focus:border-[#103D4B] focus:ring-2 focus:ring-[#103D4B] transition-all outline-none"
                    placeholder="Enter your password"
                    style={{ borderColor: "#103D4B" }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                onClick={handleLogin}
                className="w-full bg-gradient-to-r from-[#103D4B] to-[#103D4B] text-white py-3 rounded-xl font-semibold hover:from-[#103D4B] hover:to-[#103D4B] transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                Sign In
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}